import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAuth, ROLE_PREVIEW_TARGETS } from '../contexts/AuthContext';

/**
 * DIA-2 §2.3 — Role Preview toggle.
 *
 * Compact text-labelled dropdown intended to sit next to the Monitor /
 * Smartphone icon in the desktop admin header, the mobile portal header,
 * and the Site Diary header.
 *
 * Renders ONLY when:
 *   - GET /api/config { role_preview_enabled: true } (backend flag on), AND
 *   - the real logged-in user is an admin — evaluated as
 *     (isAdmin || isImpersonating) because impersonation flips the client
 *     `isAdmin` flag to false, but the backend still enforces admin-only
 *     access to the header (utils/auth.py).
 *
 * When active (previewing), the button fills Lux Orange so the admin is
 * visually reminded they are not in their real role.
 */

export const ROLE_LABELS = {
  admin:          'Admin',
  surveyor:       'Surveyor',
  operative:      'Operative',
  sub_contractor: 'Sub-contractor',
};

const LUX_ORANGE = '#F5A623';
const LUX_ORANGE_INK = '#1E2530';

export function RolePreviewToggle({ theme = 'dark' }) {
  const {
    rolePreviewEnabled, isAdmin, isImpersonating,
    impersonateRole, setImpersonateRole,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Gate strictly on backend flag + real admin. The isImpersonating branch
  // lets an admin who just impersonated see the toggle so they can exit.
  if (!rolePreviewEnabled) return null;
  if (!(isAdmin || isImpersonating)) return null;

  const currentLabel = impersonateRole ? ROLE_LABELS[impersonateRole] : ROLE_LABELS.admin;
  const isActive = !!impersonateRole;

  const pick = (role) => {
    if (role === 'admin') setImpersonateRole(null);
    else                  setImpersonateRole(role);
    setOpen(false);
  };

  const options = [
    { key: 'admin',    label: 'Admin (you)' },
    ...ROLE_PREVIEW_TARGETS.map(r => ({ key: r, label: ROLE_LABELS[r] })),
  ];

  const isDark = theme === 'dark';
  const btnBg   = isActive ? LUX_ORANGE : (isDark ? 'rgba(255,255,255,0.05)' : '#FFFFFF');
  const btnFg   = isActive ? LUX_ORANGE_INK : (isDark ? '#E5E7EB' : '#1F2937');
  const btnBrd  = isActive ? LUX_ORANGE : (isDark ? 'rgba(255,255,255,0.15)' : '#E5E7EB');
  const menuBg  = isDark ? '#2A3240' : '#FFFFFF';
  const menuBrd = isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB';
  const optFg   = isDark ? '#E5E7EB' : '#1F2937';
  const optHov  = isDark ? 'rgba(255,255,255,0.05)' : '#F9FAFB';

  return (
    <div ref={boxRef} className="relative inline-block" data-testid="role-preview-toggle">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-xs font-semibold px-2.5 py-1.5 rounded-md flex items-center gap-1 transition-colors"
        style={{ background: btnBg, color: btnFg, border: `1px solid ${btnBrd}` }}
        title={isActive ? `Previewing as ${currentLabel} — click to change` : 'Preview as another role'}
        data-testid="role-preview-toggle-btn"
      >
        <span data-testid="role-preview-current-label">{currentLabel}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 min-w-[10rem] rounded-md overflow-hidden z-50 shadow-lg"
          style={{ background: menuBg, border: `1px solid ${menuBrd}` }}
          data-testid="role-preview-menu"
        >
          {options.map(({ key, label }) => {
            const active = key === (impersonateRole || 'admin');
            return (
              <button
                key={key}
                type="button"
                onClick={() => pick(key)}
                className="w-full text-left text-[12px] font-medium px-3 py-2 flex items-center justify-between hover:opacity-90"
                style={{
                  color: optFg,
                  background: active ? optHov : 'transparent',
                }}
                data-testid={`role-preview-option-${key}`}
              >
                <span>{label}</span>
                {active && <span style={{ color: LUX_ORANGE }}>●</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RolePreviewToggle;
